import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import BrandLogo from "@/components/feature/BrandLogo";
import { IS_MOCK } from "@/services/config";
import { adminDemoAccount, staffDemoAccount } from "@/mocks/adminData";

interface AdminLoginProps {
  /** Role of an account already signed in on this browser, if any. */
  currentRole?: string;
  currentName?: string;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";

export default function AdminLogin({ currentRole, currentName }: AdminLoginProps) {
  const { login, logout } = useAuth();
  const { settings } = useSiteSettings();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const account = await login(email.trim(), password);
      if (account.role !== "admin" && account.role !== "staff") {
        logout();
        setError("This account doesn't have console access. Please use the member sign-in instead.");
      }
      // On success the console re-renders automatically — no redirect needed.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to sign in. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const demoAccounts = [
    { label: "Administrator", ...adminDemoAccount },
    { label: "Staff", ...staffDemoAccount },
  ];

  return (
    <>
      <Seo title="Admin Sign In" description="Staff and administrator sign-in for the back office console." path="/admin" />
      <div className="flex min-h-screen">
        <div className="hidden lg:flex lg:w-1/2 relative">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20moody%20photographic%20still%20life%20of%20a%20wooden%20desk%20in%20a%20back%20office%20with%20a%20brass%20desk%20lamp%2C%20ledger%20book%2C%20fountain%20pen%20and%20a%20small%20street%20map%2C%20deep%20amber%20and%20charcoal%20tones%2C%20editorial%20photography%2C%20strong%20contrast%2C%20soft%20light%20pool&width=1200&height=1400&seq=myyp-admin-login-bg&orientation=portrait"
            alt="Back office desk"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/60 to-foreground-950/45"></div>
          <div className="relative z-10 flex flex-col justify-between p-10 text-background-50">
            <Link to="/" className="flex items-center" aria-label={settings.siteName}>
              <BrandLogo className="h-14" tone="dark" />
            </Link>
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-background-50/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
                <i className="ri-shield-keyhole-line"></i> Restricted access
              </p>
              <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight">
                The back-office console
              </h2>
              <p className="mt-3 max-w-md text-sm text-background-50/80 leading-relaxed">
                Administrators and staff manage listings, approvals, reviews and every business
                on {settings.siteName} from here.
              </p>
            </div>
          </div>
        </div>

        <div className="flex w-full lg:w-1/2 items-center justify-center bg-background-50 px-4 py-12">
          <div className="w-full max-w-md">
            <Link to="/" className="mb-8 flex items-center lg:hidden" aria-label={settings.siteName}>
              <BrandLogo className="h-12" />
            </Link>

            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-secondary-900">
              <i className="ri-shield-user-line"></i> Admin console
            </span>
            <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950">Staff &amp; admin sign in</h1>
            <p className="mt-2 text-sm text-foreground-600">
              Not a team member?{" "}
              <Link to="/login" className="font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
                Go to member sign-in
              </Link>
            </p>

            {currentRole === "owner" && (
              <div className="mt-5 flex items-start gap-3 rounded-md border border-accent-300 bg-accent-50 p-3.5 text-sm text-accent-900">
                <i className="ri-information-line mt-0.5 text-lg text-accent-600"></i>
                <span>
                  You&apos;re currently signed in as{" "}
                  <span className="font-semibold">{currentName || "a member"}</span>. Sign in with an
                  administrator or staff account to open the console.
                </span>
              </div>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="admin-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  Work email
                </label>
                <input
                  id="admin-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@mysoreyellowpages.com"
                  className={inputClass}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="admin-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-xs font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="admin-password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`${inputClass} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 cursor-pointer"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-3.5 text-sm text-secondary-900">
                  <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-bold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {submitting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-shield-keyhole-line"></i>}
                {submitting ? "Verifying…" : "Sign in to console"}
              </button>
            </form>

            {IS_MOCK && (
              <div className="mt-6 rounded-md border border-dashed border-background-300 bg-background-100/70 p-4">
                <p className="flex items-center gap-2 text-xs font-semibold text-foreground-700">
                  <i className="ri-information-line text-primary-600"></i> Demo accounts (preview mode)
                </p>
                <ul className="mt-2 space-y-1 text-xs text-foreground-600">
                  {demoAccounts.map((a) => (
                    <li key={a.email}>
                      <span className="font-semibold text-foreground-800">{a.label}:</span>{" "}
                      <span className="font-mono text-foreground-900">{a.email}</span> /{" "}
                      <span className="font-mono text-foreground-900">{a.password}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex flex-wrap gap-2">
                  {demoAccounts.map((a) => (
                    <button
                      key={a.email}
                      type="button"
                      onClick={() => {
                        setEmail(a.email);
                        setPassword(a.password);
                      }}
                      className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-primary-700 hover:border-primary-400 cursor-pointer"
                    >
                      <i className="ri-magic-line"></i> Fill {a.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-6 text-center text-xs text-foreground-500">
              <Link to="/" className="hover:text-foreground-800 cursor-pointer">← Back to {settings.siteName}</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}